import { assign, fromPromise, setup } from "xstate";
import { TaskApi } from "~/js/network/TaskApi";
import type { IStatusResponse, ITaskDoneStatusResponse, ITaskStatuses } from "../helpers/types";
import type { INodeStartTrainingSchema } from "../helpers/types/INodeDefinition";
/**
 * RetryMachine State Machine
 */
export const retryMachine = setup({
	types: {
		context: {
			retryAmount: 0
		} as {
			retryAmount: number;
			lastResponse: IStatusResponse | undefined;
			lastError: unknown;
			taskId: number;
		},
		input: {} as {
			taskId: number;
		},
		output: {} as {
			lastResponse: IStatusResponse | undefined;
		}
	},
	delays: {
		retryDelay: 5000
	},
	guards: {
		/**
		 * Checks if retry limit is exceeded.
		 * @param state - State Machine state
		 * @returns {boolean}
		 */
		isRetryLimitExceeded: (state) => state.context.retryAmount > 5
	},
	actors: {
		/**
		 * fetch function implimetation that we need to retry
		 * @param {Object} params - Parameters
		 * @param {number} params.input - Task ID
		 * @returns {Promise<any>}
		 */
		fetchImpl: fromPromise(async ({ input }: { input: number }) => {
			return TaskApi.getTaskStatus(input);
		})
	}
}).createMachine({
	id: "RetryMachine",
	initial: "GetStatus",
	context: ({ input }) => ({
		retryAmount: 0,
		lastResponse: undefined,
		lastError: undefined,
		taskId: input.taskId
	}),
	output: ({ context }) => ({
		lastResponse: context.lastResponse
	}),

	states: {
		GetStatus: {
			invoke: {
				src: "fetchImpl",
				input: ({ context }) => context.taskId,
				onDone: {
					target: "RetrySuccsess",
					actions: [assign({ lastResponse: ({ event }) => event.output })]
				},
				onError: {
					actions: [
						assign({
							retryAmount: ({ context }) => context.retryAmount + 1,
							lastError: ({ event }) => event.error
						})
					],
					target: "DetermineRetryLimitExceeded"
				}
			}
		},
		WaitForRetry: {
			after: {
				retryDelay: "GetStatus"
			}
		},
		DetermineRetryLimitExceeded: {
			always: [
				{
					guard: "isRetryLimitExceeded",
					actions: ({ context }) => {
						throw context.lastError;
					},
					target: "RetryFailed"
				},
				{
					target: "WaitForRetry"
				}
			]
		},
		RetrySuccsess: {
			type: "final"
		},
		RetryFailed: {
			type: "final"
		}
	}
});

export const taskMonitoringMachine = setup({
	types: {
		context: {
			lastTaskStatus: "new"
		} as {
			taskId: number | undefined;
			taskStatus?: IStatusResponse | undefined;
			lastTaskStatus?: ITaskStatuses;
			taskData: INodeStartTrainingSchema;
			errorTaskStatuses: Record<string, any>;
			taskStatusesToTimeout: Record<string, number>;
			lastStatusChangeTs: number;
			abortController: AbortController;
			taskCreatingTs: number;
		},
		input: {} as {
			taskData: INodeStartTrainingSchema;
			errorTaskStatuses: Record<string, any>;
			taskStatusesToTimeout: Record<string, number>;
			abortController: AbortController;
		},
		output: {} as ITaskDoneStatusResponse
	},
	actions: {
		/**
		 * Handles task creation error.
		 */
		onTaskCreatingError: (_state, _error: unknown) => {},
		/**
		 * Handles task get status error.
		 */
		onTaskGetStatusError: (_state, _error: unknown) => {},
		onTaskRetryStart: (_state) => {},
		onTaskRetrySucsess: (_state) => {},
		onTaskRetryError: (_state, _error: unknown) => {},
		onTaskErroredStatus: (_state) => {},
		onTaskStatusChange: (_state) => {},
		onTaskPositionChange: (_state) => {},
		onSucsess: (_state) => {},
		onTaskCreatingSucsess: (_state) => {},
		onFailed: (_state) => {},
		onFirstPendingStatus: (_state) => {},
		onTaskStaled: (_state) => {}
	},
	guards: {
		taskCompleted: (state) => {
			return state.context.taskStatus?.status === "done";
		},
		taskErrored: (state) => {
			return (
				state.context.taskStatus !== undefined &&
				state.context.taskStatus.status in state.context.errorTaskStatuses
			);
		},
		taskStaled: (state) => {
			if (state.context.taskStatus === undefined) {
				return false;
			}
			const status = state.context.taskStatus.status;
			if (!(status in state.context.taskStatusesToTimeout)) {
				return false;
			}
			const timeout = state.context.taskStatusesToTimeout[status];

			return Date.now() - state.context.lastStatusChangeTs > timeout;
		},
		isNewTaskStatus: (state) => {
			if (state.context.taskStatus?.status === state.context.lastTaskStatus) {
				return false;
			}
			return true;
		}
	},
	actors: {
		createTask: fromPromise(
			async ({
				input
			}: {
				input: {
					schema: INodeStartTrainingSchema;
					signal: AbortSignal;
				};
			}) => {
				return TaskApi.postStartTraining(input.schema, input.signal);
			}
		),
		cancelTask: fromPromise(async ({ input }: { input: { taskId: number | undefined } }) => {
			console.log("cancelling task", input);
			return true;
		}),
		getTaskStatus: fromPromise(
			async ({ input }: { input: { taskId: number; signal: AbortSignal } }) => {
				return TaskApi.getTaskStatus(input.taskId, input.signal);
			}
		),
		retryMachine
	}
}).createMachine({
	id: "Taskmonitoring",
	initial: "SubmitTask",
	context: ({ input }) => ({
		lastTaskStatus: "new",
		taskId: undefined,
		taskStatus: undefined,
		lastStatusChangeTs: Date.now(),
		taskData: input.taskData,
		errorTaskStatuses: input.errorTaskStatuses,
		taskStatusesToTimeout: input.taskStatusesToTimeout,
		abortController: input.abortController,
		taskCreatingTs: Date.now()
	}),
	output: ({ context }) => context.taskStatus as ITaskDoneStatusResponse,

	states: {
		SubmitTask: {
			invoke: {
				src: "createTask",
				input: ({ context }) => ({
					schema: context.taskData,
					signal: context.abortController.signal
				}),
				onDone: {
					target: "GetTaskStatus",
					actions: [
						assign({
							taskId: ({ event }) => event.output.task_id,
							taskCreatingTs: Date.now()
						}),
						"onTaskCreatingSucsess"
					]
				},
				onError: {
					target: "TaskFailed",
					actions: [
						{
							type: "onTaskCreatingError",
							params: ({ event }: any) => event.error
						}
					]
				}
			}
		},
		WaitForCompletion: {
			after: {
				3000: "GetTaskStatus"
			}
		},
		GetTaskStatus: {
			invoke: {
				src: "getTaskStatus",
				input: ({ context }) => ({
					taskId: context.taskId as number,
					signal: context.abortController.signal
				}),
				onDone: {
					target: "DetermineTaskStaled",
					actions: [
						assign({
							taskStatus: ({ event }) => event.output,
							lastStatusChangeTs: ({ event, context }) => {
								if (context.taskStatus?.status === event.output.status) {
									return context.lastStatusChangeTs;
								}
								return Date.now();
							}
						}),
						"onTaskPositionChange"
					]
				},
				onError: {
					actions: [
						{
							type: "onTaskGetStatusError",
							params: ({ event }: { event: any }) => {
								return event.error;
							}
						}
					],
					target: "Retry"
				}
			}
		},
		Retry: {
			always: [
				{
					actions: ["onTaskRetryStart"]
				}
			],
			invoke: {
				src: "retryMachine",
				input: ({ context }) =>
					({
						taskId: context.taskId
					}) as {
						taskId: number;
					},

				onDone: {
					actions: [
						assign({
							taskStatus: ({ event }) => event.output.lastResponse
						}),
						"onTaskRetrySucsess"
					],
					target: "DetermineTaskStaled"
				},
				onError: {
					actions: [
						{
							type: "onTaskRetryError",
							params: ({ event }: { event: any }) => {
								return event.error;
							}
						}
					],
					target: "TaskFailed"
				}
			}
		},
		DetermineTaskStaled: {
			always: [
				{
					guard: "taskStaled",
					target: "TaskFailed",
					actions: "onTaskStaled"
				},
				{
					guard: "isNewTaskStatus",
					actions: [
						"onTaskStatusChange",
						assign(({ context }) => {
							return {
								lastTaskStatus: context.taskStatus?.status
							};
						})
					],
					target: "DetermineCompletion"
				},
				{
					target: "DetermineCompletion"
				}
			]
		},
		DetermineCompletion: {
			always: [
				{
					guard: "taskCompleted",
					target: "TaskSucceeded"
				},
				{
					guard: "taskErrored",
					target: "TaskFailed",
					actions: ["onTaskErroredStatus"]
				},
				{
					target: "WaitForCompletion"
				}
			]
		},
		TaskSucceeded: {
			type: "final"
		},
		TaskFailed: {
			always: [
				{
					actions: ["onFailed"],
					target: "End"
				}
			]
		},
		End: {
			type: "final"
		}
	}
});
